/**
 * @description String 프로토타입 확장 메서드를 제공합니다.
 */

declare global {
  interface String {
    /**
     * 문자열 양 끝의 공백과 특정 문자를 제거합니다.
     * @param trimChar - 양끝에서 제거할 문자
     * @returns 트림된 문자열
     */
    trimEx: (trimChar: string) => string;

    /**
     * 특수 문자를 이스케이프 처리합니다.
     * 따옴표(', ", `)와 역슬래시를 백슬래시로 이스케이프합니다.
     * SQL 쿼리나 JavaScript 문자열에 안전하게 삽입할 때 사용합니다.
     * @returns 이스케이프된 문자열
     */
    escape: () => string;

    /**
     * 두 문자열 사이에 있는 부분 문자열을 추출합니다.
     * @param strStart - 시작 구분자
     * @param strEnd - 종료 구분자
     * @returns 시작과 종료 구분자 사이의 문자열, 없으면 빈 문자열
     */
    between: (strStart: string, strEnd: string) => string;

    /**
     * 문자열이 특정 문자로 시작하지 않으면 앞에 추가합니다.
     * @param char - 시작 문자
     * @returns 처리된 문자열
     */
    start: (char: string) => string;

    /**
     * 문자열이 특정 문자로 끝나지 않으면 뒤에 추가합니다.
     * @param char - 끝 문자
     * @returns 처리된 문자열
     */
    end: (char: string) => string;
  }
}

/**
 * 양 끝의 공백과 특정 문자를 제거합니다.
 *
 * @example
 * ```ts
 * '/api/users/'.trimEx('/'); // 'api/users'
 * '  hello  '.trimEx(''); // 'hello'
 * ```
 */
String.prototype.trimEx = function (trimChar = ''): string {
  if (!this) {
    return this;
  }

  let ret = this.trim();

  if (ret && trimChar) {
    if (ret.substring(0, trimChar.length) === trimChar) {
      ret = ret.substring(trimChar.length);
    }
    if (ret && ret.substring(this.length - trimChar.length) === trimChar) {
      ret = ret.substring(0, ret.length - trimChar.length);
    }
  }
  return ret;
};

/**
 * 특수 문자를 이스케이프 처리합니다.
 *
 * @example
 * ```ts
 * "Hello 'World'".escape(); // "Hello \\'World\\'"
 * 'Say "Hi"'.escape(); // 'Say \\"Hi\\"'
 * ```
 */
String.prototype.escape = function (): string {
  if (!this) {
    return this;
  }
  let result = this.replace(/\\/gim, `\\\\`); // \ -> \\
  result = result.replace(/'/gim, `\\'`); // ' -> \'
  result = result.replace(/"/gim, `\\"`); // " -> \"
  result = result.replace(/`/gim, '\\`'); // ` -> \`
  return result;
};

/**
 * 두 문자열 사이의 부분 문자열을 추출합니다.
 *
 * @example
 * ```ts
 * '<div>Hello</div>'.between('<div>', '</div>'); // 'Hello'
 * 'start[content]end'.between('[', ']'); // 'content'
 * ```
 */
String.prototype.between = function (strStart: string, strEnd: string): string {
  let ret = '';
  let iStart = this.indexOf(strStart);
  if (iStart >= 0) {
    iStart += strStart.length;
    const iEnd = this.indexOf(strEnd, iStart);
    if (iEnd > 0) {
      ret = this.substring(iStart, iEnd);
    }
  }
  return ret;
};

/**
 * 특정 문자로 시작하지 않으면 앞에 추가합니다.
 *
 * @example
 * ```ts
 * 'api/users'.start('/'); // '/api/users'
 * '/api/users'.start('/'); // '/api/users' (이미 있으면 그대로)
 * ```
 */
String.prototype.start = function (char: string): string {
  if (this.startsWith(char)) {
    return this.toString();
  }
  return char + this;
};

/**
 * 특정 문자로 끝나지 않으면 뒤에 추가합니다.
 *
 * @example
 * ```ts
 * '/api/users'.end('/'); // '/api/users/'
 * '/api/users/'.end('/'); // '/api/users/' (이미 있으면 그대로)
 * ```
 */
String.prototype.end = function (char: string): string {
  if (this.endsWith(char)) {
    return this.toString();
  }
  return this + char;
};

export {};
